import { Suspense, lazy } from "react";
import type { AnswersByKey, FunnelStep } from "../funnel/types";
import { getPersonaDataWithOverride, resolvePersonaOverrideFromUrl } from "../funnel/flowData";
import { getImageRenderMeta } from "./imageMeta";

type StepRendererProps = {
  step: FunnelStep;
  answers: AnswersByKey;
  onSelectSingle: (answerKey: string, optionId: string) => void;
  onToggleMulti: (answerKey: string, optionId: string) => void;
  onSetText: (answerKey: string, value: string) => void;
  onWelcomeFirstImageReadyChange?: (isReady: boolean) => void;
  onWelcomeEmbeddedSelect?: (answerKey: string, optionId: string) => void;
  onLoadingContinue: () => void;
  onPaymentContinue: () => void;
  isPaymentSubmitting: boolean;
};

const loadQuestionStep = () => import("./steps/QuestionStep");
const loadAffirmationStep = () => import("./steps/AffirmationStep");
const loadCheckoutCompleteStep = () => import("./steps/CheckoutCompleteStep");
const loadLoadingStep = () => import("./steps/LoadingStep");
const loadResultStep = () => import("./steps/ResultStep");
const loadEmailStep = () => import("./steps/EmailStep");
const loadPaymentStep = () => import("./steps/PaymentStep");

const QuestionStepLazy = lazy(async () => ({ default: (await loadQuestionStep()).QuestionStep }));
const AffirmationStepLazy = lazy(async () => ({
  default: (await loadAffirmationStep()).AffirmationStep
}));
const CheckoutCompleteStepLazy = lazy(async () => ({
  default: (await loadCheckoutCompleteStep()).CheckoutCompleteStep
}));
const LoadingStepLazy = lazy(async () => ({ default: (await loadLoadingStep()).LoadingStep }));
const ResultStepLazy = lazy(async () => ({ default: (await loadResultStep()).ResultStep }));
const EmailStepLazy = lazy(async () => ({ default: (await loadEmailStep()).EmailStep }));
const PaymentStepLazy = lazy(async () => ({ default: (await loadPaymentStep()).PaymentStep }));

export const preloadStepRendererChunk = (kind: FunnelStep["kind"] | undefined): Promise<unknown> => {
  switch (kind) {
    case "question":
      return loadQuestionStep();
    case "affirmation":
      return Promise.all([loadAffirmationStep(), loadCheckoutCompleteStep()]);
    case "loading":
      return loadLoadingStep();
    case "result":
      return loadResultStep();
    case "email":
      return loadEmailStep();
    case "payment":
      return loadPaymentStep();
    case "welcome":
    case undefined:
      return Promise.resolve();
  }
};

const getAnswerKeyForStep = (step: FunnelStep): string => {
  if (step.kind === "question" && step.answerKey) {
    return step.answerKey;
  }
  if (step.kind === "email") {
    return step.answerKey;
  }
  if (step.kind === "payment") {
    return step.answerKey;
  }
  return step.id;
};

export const StepRenderer = ({
  step,
  answers,
  onSelectSingle,
  onToggleMulti,
  onSetText,
  onWelcomeFirstImageReadyChange,
  onWelcomeEmbeddedSelect,
  onLoadingContinue,
  onPaymentContinue,
  isPaymentSubmitting
}: StepRendererProps) => {
  if (step.kind === "welcome") {
    return (
      <section className="step step-welcome">
        <div className="welcome-intro">
          <div className="welcome-headline-stack">
            <h1 className="welcome-headline" dangerouslySetInnerHTML={{ __html: step.title }} />
          </div>
          {step.description.some(Boolean) ? (
            <div className="welcome-subheadlines">
              {step.description.filter(Boolean).map((line, i) => (
                <p key={i} className="welcome-subheadline">
                  {line}
                </p>
              ))}
            </div>
          ) : null}
          <div className="welcome-trust-badge">
            <svg className="welcome-laurel welcome-laurel-left" width="30" height="44" viewBox="0 0 36 52" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M 22 48 Q 8 26 18 4" stroke="#c9974a" strokeWidth="1.2" fill="none" strokeLinecap="round" />
              <path d="M 18.0 40.7 Q 15.8 36.9 11.5 36.0 Q 13.7 39.8 18.0 40.7 Z" fill="#c9974a" />
              <path d="M 18.0 40.7 Q 20.2 36.9 18.8 32.7 Q 16.6 36.5 18.0 40.7 Z" fill="#c9974a" />
              <path d="M 15.3 33.3 Q 13.8 29.2 9.7 27.7 Q 11.2 31.8 15.3 33.3 Z" fill="#c9974a" />
              <path d="M 15.3 33.3 Q 18.1 29.9 17.4 25.6 Q 14.6 29.0 15.3 33.3 Z" fill="#c9974a" />
              <path d="M 14.0 26.0 Q 13.2 21.7 9.4 19.5 Q 10.2 23.8 14.0 26.0 Z" fill="#c9974a" />
              <path d="M 14.0 26.0 Q 17.3 23.1 17.4 18.7 Q 14.0 21.6 14.0 26.0 Z" fill="#c9974a" />
              <path d="M 14.0 18.7 Q 14.0 14.3 10.6 11.4 Q 10.7 15.8 14.0 18.7 Z" fill="#c9974a" />
              <path d="M 14.0 18.7 Q 17.8 16.4 18.6 12.1 Q 14.8 14.4 14.0 18.7 Z" fill="#c9974a" />
              <path d="M 15.3 11.3 Q 16.1 7.0 13.3 3.6 Q 12.6 7.9 15.3 11.3 Z" fill="#c9974a" />
              <path d="M 15.3 11.3 Q 19.4 9.8 21.0 5.7 Q 16.9 7.2 15.3 11.3 Z" fill="#c9974a" />
              <path d="M 18.0 4.0 Q 21.5 0.6 21.7 -4.2 Q 18.2 -0.8 18.0 4.0 Z" fill="#c9974a" />
            </svg>
            <div className="welcome-trust-inner">
              <div className="welcome-trust-rating-row">
                <span className="welcome-trust-rule" aria-hidden="true" />
                <span className="welcome-trust-count" dangerouslySetInnerHTML={{ __html: step.ratingText }} />
                <span className="welcome-trust-rule" aria-hidden="true" />
              </div>
              {step.trustBadgeAvatars?.length ? (
                <div className="welcome-trust-avatars" aria-hidden="true">
                  {step.trustBadgeAvatars.map((avatar, index) => (
                    <img
                      key={`${avatar.src}-${index}`}
                      src={avatar.src}
                      alt=""
                      className="welcome-trust-avatar"
                      width={26}
                      height={26}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
              ) : null}
              {step.trustBadgeCaption ? (
                <p className="welcome-trust-desc">{step.trustBadgeCaption}</p>
              ) : null}
            </div>
            <svg className="welcome-laurel welcome-laurel-right" width="30" height="44" viewBox="0 0 36 52" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M 22 48 Q 8 26 18 4" stroke="#c9974a" strokeWidth="1.2" fill="none" strokeLinecap="round" />
              <path d="M 18.0 40.7 Q 15.8 36.9 11.5 36.0 Q 13.7 39.8 18.0 40.7 Z" fill="#c9974a" />
              <path d="M 18.0 40.7 Q 20.2 36.9 18.8 32.7 Q 16.6 36.5 18.0 40.7 Z" fill="#c9974a" />
              <path d="M 15.3 33.3 Q 13.8 29.2 9.7 27.7 Q 11.2 31.8 15.3 33.3 Z" fill="#c9974a" />
              <path d="M 15.3 33.3 Q 18.1 29.9 17.4 25.6 Q 14.6 29.0 15.3 33.3 Z" fill="#c9974a" />
              <path d="M 14.0 26.0 Q 13.2 21.7 9.4 19.5 Q 10.2 23.8 14.0 26.0 Z" fill="#c9974a" />
              <path d="M 14.0 26.0 Q 17.3 23.1 17.4 18.7 Q 14.0 21.6 14.0 26.0 Z" fill="#c9974a" />
              <path d="M 14.0 18.7 Q 14.0 14.3 10.6 11.4 Q 10.7 15.8 14.0 18.7 Z" fill="#c9974a" />
              <path d="M 14.0 18.7 Q 17.8 16.4 18.6 12.1 Q 14.8 14.4 14.0 18.7 Z" fill="#c9974a" />
              <path d="M 15.3 11.3 Q 16.1 7.0 13.3 3.6 Q 12.6 7.9 15.3 11.3 Z" fill="#c9974a" />
              <path d="M 15.3 11.3 Q 19.4 9.8 21.0 5.7 Q 16.9 7.2 15.3 11.3 Z" fill="#c9974a" />
              <path d="M 18.0 4.0 Q 21.5 0.6 21.7 -4.2 Q 18.2 -0.8 18.0 4.0 Z" fill="#c9974a" />
            </svg>
          </div>
          {step.featuredIn?.length ? (
            <div className="welcome-featured-in" aria-label="As featured in">
              <span className="welcome-featured-in-label">Featured in</span>
              <img
                src="/cooky/Collateral/Credibility_logos-166.webp"
                srcSet="/cooky/Collateral/Credibility_logos-166.webp 1x, /cooky/Collateral/Credibility_logos-249.webp 1.5x"
                alt="Men's Health, POPSUGAR, The Kitchn"
                className="welcome-featured-in-logos"
                width={166}
                height={13}
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : null}
        </div>
        {step.embeddedQuestion ? (
          <div className="welcome-embedded-question">
            <div className="welcome-embedded-question-heading">
              <h2 className="welcome-embedded-question-title">{step.embeddedQuestion.title}</h2>
              <img
                src="/cooky/elements/arrow-down.png"
                alt=""
                className="welcome-embedded-question-arrow-img"
                width={46}
                height={177}
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="welcome-embedded-options">
              {step.embeddedQuestion.options.map((option) => (
                (() => {
                  const optionImageMeta = getImageRenderMeta(option.image);

                  return (
                    <button
                      key={option.id}
                      type="button"
                      className="option-card welcome-embedded-option-card"
                      onClick={() =>
                        (onWelcomeEmbeddedSelect ?? onSelectSingle)(
                          step.embeddedQuestion!.answerKey,
                          option.id
                        )
                      }
                    >
                      <div className="welcome-embedded-option-media">
                        {option.image ? (
                          <img
                            src={option.image}
                            width={optionImageMeta?.width}
                            height={optionImageMeta?.height}
                            alt=""
                            className="welcome-embedded-option-image"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : null}
                      </div>
                      <div className="welcome-embedded-option-copy">
                        <span className="option-label">{option.label}</span>
                        {option.description ? (
                          <span className="welcome-embedded-option-description">
                            {option.description}
                          </span>
                        ) : null}
                      </div>
                      <span className="welcome-embedded-option-chevron" aria-hidden>
                        ›
                      </span>
                    </button>
                  );
                })()
              ))}
            </div>
            <p className="welcome-embedded-trust">No commitment · 60 seconds · No spam</p>
          </div>
        ) : step.heroVideo ? (
          <div className="welcome-hero-static">
            <video
              className="welcome-hero-image welcome-hero-video"
              src={step.heroVideo}
              poster={step.heroImage}
              autoPlay
              muted
              loop
              playsInline
              onLoadedData={() => onWelcomeFirstImageReadyChange?.(true)}
              onError={() => onWelcomeFirstImageReadyChange?.(true)}
            />
          </div>
        ) : step.heroImage ? (
          <div className="welcome-hero-static">
            <img
              src={step.heroImage}
              alt=""
              className="welcome-hero-image welcome-hero-image-rounded"
              width={600}
              height={751}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              onLoad={() => onWelcomeFirstImageReadyChange?.(true)}
              onError={() => onWelcomeFirstImageReadyChange?.(true)}
            />
          </div>
        ) : null}
      </section>
    );
  }

  if (step.kind === "question") {
    const answerKey = getAnswerKeyForStep(step);
    const value = answers[answerKey];
    return (
      <Suspense fallback={null}>
        <QuestionStepLazy
          step={step}
          value={typeof value === "string" || Array.isArray(value) ? value : undefined}
          onSelectSingle={(optionId) => onSelectSingle(answerKey, optionId)}
          onToggleMulti={(optionId) => onToggleMulti(answerKey, optionId)}
        />
      </Suspense>
    );
  }

  if (step.kind === "affirmation") {
    return (
      <Suspense fallback={null}>
        {step.id === "checkout_complete" ? (
          <CheckoutCompleteStepLazy step={step} />
        ) : (
          <AffirmationStepLazy step={step} />
        )}
      </Suspense>
    );
  }

  if (step.kind === "loading") {
    const modalValue = answers[step.modalQuestion.answerKey];
    return (
      <Suspense fallback={null}>
        <LoadingStepLazy
          step={step}
          modalValue={typeof modalValue === "string" ? modalValue : undefined}
          onSelectModal={(optionId) => onSelectSingle(step.modalQuestion.answerKey, optionId)}
          onContinue={onLoadingContinue}
        />
      </Suspense>
    );
  }

  if (step.kind === "result") {
    const personaOverride = resolvePersonaOverrideFromUrl();
    const personaData = getPersonaDataWithOverride(answers, personaOverride);
    return (
      <Suspense fallback={null}>
        <ResultStepLazy step={{ ...step, ...personaData }} />
      </Suspense>
    );
  }

  if (step.kind === "email") {
    const answerKey = getAnswerKeyForStep(step);
    const email = answers[answerKey];
    return (
      <Suspense fallback={null}>
        <EmailStepLazy
          step={step}
          email={typeof email === "string" ? email : ""}
          onEmailChange={(value) => onSetText(answerKey, value)}
        />
      </Suspense>
    );
  }

  const answerKey = getAnswerKeyForStep(step);
  const selectedPlan = answers[answerKey];

  return (
    <Suspense fallback={null}>
      <PaymentStepLazy
        step={step}
        selectedPlan={typeof selectedPlan === "string" ? selectedPlan : ""}
        onSelectPlan={(planId) => onSetText(answerKey, planId)}
        onProceedPayment={onPaymentContinue}
        isSubmitting={isPaymentSubmitting}
      />
    </Suspense>
  );
};
